import {useEffect, useState} from 'react';
import {graphql} from 'react-relay';
import {useForecastFetchQuery} from './useForecastFetchQuery';
import type {usePortalEligibleApproversQuery} from './__generated__/usePortalEligibleApproversQuery.graphql';

const eligibleApproversQuery = graphql`
	query usePortalEligibleApproversQuery($projectId: String!) {
		viewer {
			project(id: $projectId) {
				portalEligibleApprovers {
					portalUserId
					email
					roleLabel
				}
			}
		}
	}
`;

/** One selectable sign-off approver: a client workspace user, identified by email. */
export type PortalEligibleApprover = {
	/** portal_users UUID — the value persisted as the approver. Not a Relay global id. */
	portalUserId: string;
	/** The label. portal_users carries no name column, so the email is the only always-present one. */
	email: string;
	/** 'Client Executive' / 'Active Collaborator' — shown so a PM can see why someone is offerable. */
	roleLabel: string | null;
};

export type PortalEligibleApproversResult = {
	approvers: PortalEligibleApprover[];
	loading: boolean;
	/**
	 * The fetch came back rejected. Load failures and "this client org has nobody
	 * eligible" both leave `approvers` empty, and they call for opposite advice — go
	 * provision a user, versus try again. `useForecastFetchQuery` surfaces no error
	 * state of its own (by design; it is the thin imperative hook, not
	 * ForecastQueryRenderer), so the distinction is tracked here or not at all.
	 */
	error: boolean;
};

/**
 * FOR-525: the client workspace users who may sign off work on `projectId`.
 *
 * Fetched imperatively rather than through the host view's own query so the sign-off
 * approver picker can be dropped into any phase row or task row without every entry
 * point into Manage Phases having to carry the list in its fragment. That is the
 * sanctioned use of `useForecastFetchQuery` (a dropdown's options), not a view-loading
 * query — see AGENTS.md "Relay (Web-Front)".
 *
 * Call it ONCE per surface and pass the result to every picker on it. Each call is a
 * network round trip, so hooking it inside the picker itself would issue one request
 * per phase row.
 *
 * `projectId` is the project's Relay global id. Null/absent yields an empty,
 * not-loading, not-failed result — the picker then renders its empty state, which is
 * the honest answer when we do not know which project is being configured.
 */
export const usePortalEligibleApprovers = (projectId: string | null | undefined): PortalEligibleApproversResult => {
	const {data, fetch, loading} = useForecastFetchQuery<usePortalEligibleApproversQuery['response'], {projectId: string}>(
		eligibleApproversQuery
	);
	const [error, setError] = useState(false);

	useEffect(() => {
		if (!projectId) return;
		setError(false);
		fetch(
			{projectId},
			() => setError(false),
			() => setError(true)
		);
	}, [projectId]);

	const edges = data?.viewer?.project?.portalEligibleApprovers ?? null;
	const approvers: PortalEligibleApprover[] = (edges ?? []).flatMap(approver =>
		approver && approver.portalUserId && approver.email
			? [{portalUserId: approver.portalUserId, email: approver.email, roleLabel: approver.roleLabel ?? null}]
			: []
	);

	return {approvers, loading: !!projectId && loading, error: !!projectId && error};
};
